import { motion } from "framer-motion";
import { FileText, Download, ShieldCheck } from "lucide-react";

const FORMS = [
  {
    title: "Athlete Liability Waiver",
    badge: "18+",
    desc: "Required for all athletes 18 years of age or older before participating in any evaluation or training session.",
    file: "/JJSF-Athlete-Waiver-18plus.pdf",
    filename: "JJSF-Athlete-Waiver-18plus.pdf",
  },
  {
    title: "Parent / Guardian Liability Waiver",
    badge: "Under 18",
    desc: "Required for all athletes under 18. Must be completed and signed by a parent or legal guardian before participation.",
    file: "/JJSF-Parent-Guardian-Waiver-Under18.pdf",
    filename: "JJSF-Parent-Guardian-Waiver-Under18.pdf",
  },
];

export default function RequiredForms() {
  return (
    <section
      id="forms"
      data-testid="forms-section"
      className="py-16 md:py-36 bg-black border-t border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl mb-6"
        >
          <p className="font-mono-accent text-xs tracking-[0.35em] uppercase text-white/40 mb-6">
            Before You Train
          </p>
          <h2 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-5xl md:text-7xl">
            Required Forms
          </h2>
        </motion.div>

        <div className="flex items-start gap-3 mb-12 text-white/60 max-w-2xl" data-testid="forms-reminder">
          <ShieldCheck size={20} className="text-white/70 shrink-0 mt-0.5" />
          <p>
            All required waivers must be completed before your first evaluation or
            training session. Download, fill out, and bring the correct form for the
            athlete's age.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {FORMS.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`form-card-${i}`}
              className="group flex flex-col bg-[#0a0a0a] border border-white/10 hover:border-white/30 transition-all duration-500 hover:-translate-y-1 p-8"
            >
              <div className="flex items-center justify-between mb-6">
                <FileText size={30} className="text-white/80" strokeWidth={1.5} />
                <span className="font-mono-accent text-[10px] tracking-[0.2em] uppercase border border-white/25 text-white/70 px-3 py-1">
                  {f.badge}
                </span>
              </div>
              <h3 className="font-display font-bold uppercase tracking-tight text-2xl leading-none mb-3">
                {f.title}
              </h3>
              <p className="text-white/55 text-sm flex-1">{f.desc}</p>
              <p className="mt-4 font-mono-accent text-[11px] tracking-[0.15em] uppercase text-white/35">
                Fillable PDF · Includes waiver, medical, emergency contact & photo/video release
              </p>
              <a
                data-testid={`form-download-${i}`}
                href={f.file}
                download={f.filename}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex items-center justify-center gap-2 bg-white text-black uppercase tracking-[0.15em] font-bold text-xs py-4 hover:bg-white/85 transition-colors duration-300"
              >
                <Download size={16} /> Download Form
              </a>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
